import { separatedGeolocationCodec } from '@/modules/location-groups/domain/geolocationCodecs'
import {
  GEOLOCATION_AVAILABILITY_TYPES,
  type GeolocationApiModel,
  type GeolocationAvailabilityType,
  type LeafletGeolocationApiModel
} from '@/types/GeolocationModel'
import {
  DEFAULT_GEOLOCATION_AVAILABILITY_TYPE,
  extractCoordinates,
  formatGeolocation
} from '@/views/dashboard/domain/geolocations'

type WithGeolocationsArray = { geolocations: GeolocationApiModel[] }
type GeolocationsModelValue = string | GeolocationApiModel[] | null | undefined
type WithGeolocationsModel<T = object> = T & { geolocations?: GeolocationsModelValue }

export type GeolocationsAdapterOptions = {
  includeAvailabilityType?: boolean
}

export function leafletGeolocationTextareasToApi(
  availableGeolocations: string,
  unavailableGeolocations: string
): LeafletGeolocationApiModel[] {
  return [
    ...separatedGeolocationCodec.parse(availableGeolocations, 'AVAILABLE').geolocations,
    ...separatedGeolocationCodec.parse(unavailableGeolocations, 'UNAVAILABLE').geolocations
  ]
}

function normalizeAvailabilityType(availabilityType: unknown): GeolocationAvailabilityType | null {
  if (availabilityType == null) return DEFAULT_GEOLOCATION_AVAILABILITY_TYPE

  return GEOLOCATION_AVAILABILITY_TYPES.find(type => type === availabilityType) ?? null
}

function geolocationsApiToString(
  apiArr: GeolocationApiModel[] = [],
  { includeAvailabilityType = false }: GeolocationsAdapterOptions = {}
): string {
  return apiArr
    .map(geolocation =>
      formatGeolocation(
        {
          ...geolocation,
          availabilityType:
            normalizeAvailabilityType(geolocation.availabilityType) ?? DEFAULT_GEOLOCATION_AVAILABILITY_TYPE
        },
        includeAvailabilityType
      )
    )
    .join('\n')
}

function normalizeGeolocationItem(
  geolocation: GeolocationApiModel,
  includeAvailabilityType: boolean
): GeolocationApiModel | LeafletGeolocationApiModel | null {
  const lat = Number(geolocation.lat)
  const lng = Number(geolocation.lng)
  const radius = Number(geolocation.radius)

  if (isNaN(lat) || isNaN(lng) || isNaN(radius)) return null

  const normalized = { lat, lng, radius, ...(geolocation.name ? { name: geolocation.name } : {}) }

  if (!includeAvailabilityType) return normalized

  const availabilityType = normalizeAvailabilityType(geolocation.availabilityType)
  if (availabilityType === null) return null

  return {
    ...normalized,
    availabilityType
  }
}

function geolocationsArrayToApi(
  geolocations: GeolocationApiModel[],
  includeAvailabilityType: boolean
): GeolocationApiModel[] {
  return geolocations
    .map(geolocation => normalizeGeolocationItem(geolocation, includeAvailabilityType))
    .filter((geolocation): geolocation is GeolocationApiModel => geolocation !== null)
}

function geolocationsStringToApi(str: string, includeAvailabilityType: boolean): GeolocationApiModel[] {
  if (!str) return []

  const trimmed = str.trim()

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)

      if (Array.isArray(parsed)) {
        return geolocationsArrayToApi(parsed, includeAvailabilityType)
      }
    } catch {
      return []
    }
  }

  return extractCoordinates(str, { includeAvailabilityType }).map(geolocation => {
    if (includeAvailabilityType) return geolocation

    return {
      lat: geolocation.lat,
      lng: geolocation.lng,
      radius: geolocation.radius
    }
  })
}

function geolocationsToApi(
  geolocations: GeolocationsModelValue,
  includeAvailabilityType: boolean
): GeolocationApiModel[] {
  if (Array.isArray(geolocations)) {
    return geolocationsArrayToApi(geolocations, includeAvailabilityType)
  }

  return geolocationsStringToApi(geolocations ?? '', includeAvailabilityType)
}

export function geolocationsApiToModel<T extends WithGeolocationsArray>(
  api: T,
  options: GeolocationsAdapterOptions = {}
) {
  return {
    ...api,
    geolocations: geolocationsApiToString(api.geolocations ?? [], options)
  }
}

export function geolocationsModelToApi<T extends WithGeolocationsModel>(
  model: T,
  options: { includeAvailabilityType: true }
): Omit<T, 'geolocations'> & { geolocations: LeafletGeolocationApiModel[] }
export function geolocationsModelToApi<T extends WithGeolocationsModel>(
  model: T,
  options?: { includeAvailabilityType?: false }
): Omit<T, 'geolocations'> & { geolocations: GeolocationApiModel[] }
export function geolocationsModelToApi<T extends WithGeolocationsModel>(
  model: T,
  { includeAvailabilityType = false }: GeolocationsAdapterOptions = {}
): Omit<T, 'geolocations'> & { geolocations: GeolocationApiModel[] } {
  return {
    ...model,
    geolocations: geolocationsToApi(model.geolocations, includeAvailabilityType)
  }
}
